import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import moment from "moment";
import { observer } from "mobx-react";
import Button from "../../atoms/Button";
import { MobiliAvatar } from "../../atoms/MobiliAvatar";
import Controls from "../../atoms/Controls";
import useStore from "../../helpers/useStore";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { UserContext } from "../../stores/User/User.provider";
import { useApi } from "../../api/ApiProvider";
import { useLocationId } from "../../stores/User/User.hooks";
import { useBackPlanningAgent } from "../../hooks/useBackPlanningAgent";
import { useAppointmentsBySeries } from "../../stores/Appointment/appointment.queries";
import { useRx } from "../../stores/Rx/rx.queries";

interface BackplanModalProps {
  open: boolean;
  onClose: () => void;
}

interface PreviewWeekday {
  day: string;
  weekdayNum: number;
  creates: number;
  moves: number;
}

interface PreviewActionItem {
  action_type: string;
  patient_name?: string;
  therapist_name?: string;
  new_therapist_name?: string;
  start?: string;
  end?: string;
  new_start?: string;
  new_end?: string;
  weekday?: number;
}

interface PreviewData {
  weekdays: PreviewWeekday[];
  actions: PreviewActionItem[];
  loaBlockedCount: number;
}

const WEEKDAY_NAMES: Record<number, string> = {
  1: "Montag",
  2: "Dienstag",
  3: "Mittwoch",
  4: "Donnerstag",
  5: "Freitag",
};

const BackplanModal: React.FC<BackplanModalProps> = observer(({ open, onClose }) => {
  const { agentTools } = useApi();
  const locationId = useLocationId();
  const CalendarStore = useStore(CalendarContext);
  const { selectedRxId } = CalendarStore;
  const UserStore = useStore(UserContext);
  const horizonDate = useMemo(() => {
    const weeks = UserStore.apiInfo?.horizonWeeks;
    if (!weeks) return null;
    return moment().add(weeks, "weeks").format("DD.MM.YYYY");
  }, [UserStore.apiInfo?.horizonWeeks]);

  // Fetch Rx to get seriesId, then fetch appointments for the full series
  const { data: rxData } = useRx(selectedRxId || "");
  const seriesId = useMemo(() => {
    if (!rxData?.therapyRxs?.length) {
      console.log("[Backplan Preflight] rxData.therapyRxs empty or missing. rxData:", rxData);
      return "";
    }
    const therapyRxs = rxData.therapyRxs as any[];
    console.log(
      "[Backplan Preflight] therapyRxs:",
      therapyRxs.map((t) => ({ id: t?.id, rxSeriesId: t?.rxSeries?.id, rxId: t?.rx?.id })),
    );

    // Prefer the therapy entry that belongs to the selected Rx from CalendarStore.
    const selectedTherapyRx = therapyRxs.find((therapyRx) => therapyRx?.rx?.id === selectedRxId);
    const selectedSeriesId = selectedTherapyRx?.rxSeries?.id;
    if (selectedSeriesId) return selectedSeriesId;

    // Fallback for payloads that don't include nested rx.id on therapy entries.
    const firstSeriesId = therapyRxs.find((therapyRx) => therapyRx?.rx?.rxSeries?.id)?.rxSeries?.id;
    return firstSeriesId || "";
  }, [rxData, selectedRxId]);
  const { data: seriesAppointments } = useAppointmentsBySeries(seriesId);

  // Compute boundary date: last appointment in the series + 2 days (min: today)
  // Excludes continuous/perpetual Rx appointments
  const computedBoundaryDate = useMemo(() => {
    if (!seriesAppointments || seriesAppointments.length === 0) {
      console.log(
        "[Backplan Preflight] No series appointments found. seriesId:",
        seriesId,
        "selectedRxId:",
        selectedRxId,
      );
      return moment().format("YYYY-MM-DD");
    }
    // Filter: only scheduled (non-cancelled) appointments with a timeslot in THIS series AND non-perpetual
    const nonPerpetualAppts = seriesAppointments.filter((apt) => {
      // Exclude cancelled appointments (they have originalDate/reason from CancelledAppointment mapping)
      if ((apt as any).originalDate || (apt as any).reason) return false;
      const timeslots = (apt as any).timeSlots || [];
      // Check if ANY timeslot belongs to this series and is non-perpetual
      return timeslots.some((ts: any) => {
        const therapyRx = ts?.therapyRx;
        return therapyRx?.rx?.rxSeries?.id === seriesId && !therapyRx?.rx?.isPerpetual;
      });
    });
    const dates = nonPerpetualAppts
      .filter((apt) => apt.start)
      .map((apt) => moment(apt.start).format("YYYY-MM-DD"))
      .sort();
    console.log(
      `[Backplan Preflight] Series ${seriesId} — ${dates.length} non-perpetual appointments:`,
      dates,
    );

    if (dates.length === 0) {
      return moment().format("YYYY-MM-DD");
    }

    const lastDate = nonPerpetualAppts.reduce((max, apt) => {
      const aptDate = apt.start ? moment(apt.start) : null;
      if (aptDate && aptDate.isAfter(max)) return aptDate;
      return max;
    }, moment(0));

    if (!lastDate.isValid() || lastDate.isSame(moment(0))) {
      return moment().format("YYYY-MM-DD");
    }
    const lastDateStr = lastDate.format("YYYY-MM-DD");
    // Add 2 weekdays (skip weekends)
    let boundary = lastDate.clone();
    let daysAdded = 0;
    while (daysAdded < 2) {
      boundary.add(1, "day");
      if (boundary.isoWeekday() <= 5) daysAdded++;
    }
    const today = moment();
    const result = (boundary.isBefore(today) ? today : boundary).format("YYYY-MM-DD");
    console.log(
      `[Backplan Preflight] Last apt: ${lastDateStr} → +2d: ${boundary.format("YYYY-MM-DD")} → final (max w/ today): ${result}`,
    );
    return result;
  }, [seriesAppointments, seriesId, selectedRxId]);

  // User override: null means "use computed value", string means "user picked this"
  const [userSelectedDate, setUserSelectedDate] = useState<string | null>(null);

  // Reset user selection when modal closes
  useEffect(() => {
    if (!open) setUserSelectedDate(null);
  }, [open]);

  // Final endDate: user's pick wins, otherwise use computed boundary
  const endDate =
    userSelectedDate ?? (selectedRxId ? computedBoundaryDate : moment().format("YYYY-MM-DD"));

  const { workflowLoading, startBackPlanning } = useBackPlanningAgent({
    onCompleted: () => {
      console.log("[Backplan] Workflow completed");
      onClose();
    },
  });

  // Preview: fetch intent summary when modal opens
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!open || !locationId) {
      setPreview(null);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);

    const fetchPreview = async () => {
      try {
        const dto: any = {
          locationId,
          endDate: moment.utc(endDate).toISOString(),
          ...(selectedRxId && { selectedRxId }),
        };
        const resp = await agentTools.previewBackplanning(dto);
        if (cancelled) return;

        const data = (resp as any)?.data || resp;

        const actions: PreviewActionItem[] = (data?.actions || []).map((a: any) => ({
          action_type: a.action_type,
          patient_name: a.patient_name,
          therapist_name: a.therapist_name,
          new_therapist_name: a.new_therapist_name,
          start: a.start,
          end: a.end,
          new_start: a.new_start,
          new_end: a.new_end,
          weekday: a.weekday,
        }));
        const weekdayMap: Record<number, { creates: number; moves: number }> = {};
        for (const a of actions) {
          const wd = a.weekday as number;
          if (!wd) continue;
          if (!weekdayMap[wd]) weekdayMap[wd] = { creates: 0, moves: 0 };
          if (a.action_type === "create") weekdayMap[wd].creates++;
          else if (a.action_type === "move") weekdayMap[wd].moves++;
        }

        const weekdays: PreviewWeekday[] = (
          (data?.affected_weekdays || data?.affectedWeekdays || []) as number[]
        ).map((wd: number) => ({
          day: WEEKDAY_NAMES[wd] || String(wd),
          weekdayNum: wd,
          creates: weekdayMap[wd]?.creates || 0,
          moves: weekdayMap[wd]?.moves || 0,
        }));

        setPreview({
          weekdays,
          actions,
          loaBlockedCount: data?.loa_blocked_count || data?.loaBlockedCount || 0,
        });
      } catch (err: any) {
        console.warn("[Backplan Preview] Failed:", err?.message || String(err));
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    fetchPreview();
    return () => {
      cancelled = true;
    };
  }, [open, locationId, endDate, selectedRxId, agentTools]);

  const handleConfirm = useCallback(async () => {
    console.log("[Backplan] Triggering with endDate:", endDate, "selectedRxId:", selectedRxId);
    await startBackPlanning({
      endDate: moment.utc(endDate).toISOString(),
      ...(selectedRxId && { selectedRxId }),
    });
  }, [startBackPlanning, endDate, selectedRxId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: 350 } }}
    >
      <DialogTitle>Backplanning starten</DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Box display="flex" flexDirection="column" gap={1.5} mt={0.5}>
          {/* Date range + explanation */}
          <Box sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 15, mb: 1 }}>
              Der Kalender wird im folgenden Zeitraum an den aktuellen Dauertermin angepasst:
            </Typography>
            {horizonDate ? (
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography variant="body1" sx={{ fontSize: 16, fontWeight: 500 }}>
                  {horizonDate}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: 20, fontWeight: 700 }}
                >
                  →
                </Typography>
                <Controls.DatePicker
                  name="endDate"
                  value={endDate}
                  minDate={moment().format("YYYY-MM-DD")}
                  onChange={(e: any) => {
                    const raw = e.target.value;
                    if (raw) {
                      setUserSelectedDate(moment(raw).format("YYYY-MM-DD"));
                    }
                  }}
                  style={{
                    width: 160,
                    "& .MuiInputBase-root": { height: 40, fontSize: 16, px: "8px" },
                    "& .MuiInputBase-input": { py: "4px", px: "4px" },
                    "& .MuiInputAdornment-root": { ml: "-8px" },
                    "& .MuiIconButton-root": { p: "4px" },
                  }}
                />
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1.5}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap", fontSize: 16 }}
                >
                  Enddatum:
                </Typography>
                <Controls.DatePicker
                  name="endDate"
                  value={endDate}
                  minDate={moment().format("YYYY-MM-DD")}
                  onChange={(e: any) => {
                    const raw = e.target.value;
                    if (raw) {
                      setUserSelectedDate(moment(raw).format("YYYY-MM-DD"));
                    }
                  }}
                  style={{
                    width: 160,
                    "& .MuiInputBase-root": { height: 38, fontSize: 15, px: "8px" },
                    "& .MuiInputBase-input": { py: "4px", px: "4px" },
                    "& .MuiInputAdornment-root": { ml: "-8px" },
                    "& .MuiIconButton-root": { p: "4px" },
                  }}
                />
              </Box>
            )}
            {!selectedRxId && (
              <Alert severity="info" sx={{ py: 0.5, mt: 1, width: "fit-content" }}>
                Um das Enddatum anhand des Patientenplans zu berechnen, wähle zuerst einen Termin
                aus.
              </Alert>
            )}
          </Box>

          {/* Preview: DT diff */}
          {previewLoading && (
            <Box mt={1} display="flex" alignItems="center" justifyContent="center">
              <CircularProgress size={40} />
            </Box>
          )}
          {!previewLoading && preview && (
            <Box sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1.5 }}>
              <Typography variant="body1" sx={{ mb: 0.25, fontWeight: 600, fontSize: 18 }}>
                {(() => {
                  const creates = preview.actions.filter((a) => a.action_type === "create").length;
                  const moves = preview.actions.filter((a) => a.action_type === "move").length;
                  const parts: string[] = [];
                  if (creates) parts.push(`${creates} neue Termine`);
                  if (moves) parts.push(`${moves} Verschiebungen`);
                  return parts.length ? parts.join(", ") : "Keine Änderungen erforderlich";
                })()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>
                Folgende Termine werden pro Wochentag erstellt oder verschoben.
              </Typography>
              {preview.weekdays
                .filter((wd) => wd.creates || wd.moves)
                .map((wd) => {
                  const dayActions = preview.actions
                    .filter((a) => a.weekday === wd.weekdayNum)
                    .sort((a, b) => (a.start || "").localeCompare(b.start || ""));
                  // Group actions by therapist
                  const byTherapist: Record<string, PreviewActionItem[]> = {};
                  for (const a of dayActions) {
                    const key = a.therapist_name || "Unbekannt";
                    if (!byTherapist[key]) byTherapist[key] = [];
                    byTherapist[key].push(a);
                  }
                  return (
                    <Accordion
                      key={wd.day}
                      disableGutters
                      elevation={0}
                      defaultExpanded
                      sx={{
                        bgcolor: "transparent",
                        "&:before": { display: "none" },
                        "&:first-of-type": { borderTop: "2px solid", borderColor: "divider" },
                        borderBottom: "2px solid",
                        borderColor: "divider",
                        mb: 1,
                        "& .MuiAccordionSummary-root": { minHeight: 32, px: 0 },
                        "& .MuiAccordionSummary-content": { my: 0 },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 16 }}>
                          <strong>{wd.day}</strong>: {wd.creates ? `${wd.creates} erstellen` : ""}
                          {wd.creates && wd.moves ? ", " : ""}
                          {wd.moves ? `${wd.moves} verschieben` : ""}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          pt: 0,
                          pb: 1,
                          pl: 1,
                          maxHeight: 230,
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {Object.entries(byTherapist).map(([therapistName, actions]) => {
                          const nameParts = therapistName.split(" ");
                          const nickName =
                            nameParts.length >= 2
                              ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
                              : therapistName.slice(0, 2);
                          return (
                            <Box
                              key={therapistName}
                              display="flex"
                              gap={0.75}
                              sx={{
                                mb: 1,
                                pb: 1,
                                position: "relative",
                                "&:not(:last-child)::after": {
                                  content: "''",
                                  position: "absolute",
                                  bottom: 0,
                                  left: "5%",
                                  width: "90%",
                                  borderBottom: "1px solid",
                                  borderColor: "divider",
                                },
                                "&:last-child": { mb: 0, pb: 0 },
                              }}
                            >
                              <Box sx={{ pt: 0.2 }}>
                                <MobiliAvatar
                                  isTherapist
                                  user={
                                    {
                                      firstName: nameParts[0],
                                      lastName: nameParts.slice(1).join(" "),
                                      nickName,
                                    } as any
                                  }
                                  sx={{ width: 28, height: 28, fontSize: "0.7rem" }}
                                />
                              </Box>
                              <Box flex={1} sx={{ columns: 2, columnGap: "4px" }}>
                                {actions.map((a, idx) => (
                                  <Box
                                    key={idx}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ py: 0.2, breakInside: "avoid" }}
                                  >
                                    {a.action_type === "create" ? (
                                      <AddIcon sx={{ fontSize: 18, color: "success.main" }} />
                                    ) : (
                                      <SwapHorizIcon sx={{ fontSize: 18, color: "warning.main" }} />
                                    )}
                                    <Typography variant="body2" noWrap sx={{ fontSize: 16 }}>
                                      {a.start && a.end
                                        ? `${a.start.slice(0, 5)}–${a.end.slice(0, 5)}`
                                        : ""}
                                      {a.action_type === "move" && a.new_start
                                        ? ` → ${a.new_start.slice(0, 5)}`
                                        : ""}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      noWrap
                                      sx={{ fontSize: 16 }}
                                    >
                                      {a.patient_name || "—"}
                                    </Typography>
                                    {a.action_type === "move" && a.new_therapist_name && (
                                      <Typography
                                        variant="body2"
                                        color="text.disabled"
                                        noWrap
                                        sx={{ ml: "auto", fontSize: 14 }}
                                      >
                                        → {a.new_therapist_name}
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              {/* {preview.loaBlockedCount > 0 && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                  {preview.loaBlockedCount} Aktion(en) erfordern KI-Umplanung (Abwesenheit)
                </Typography>
              )} */}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={workflowLoading}>
          Abbrechen
        </Button>
        <Button onClick={handleConfirm} disabled={workflowLoading || !locationId}>
          {workflowLoading ? "Läuft..." : "Bestätigen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default BackplanModal;
